<template>
    <div>
        <div
            v-if="confirmationIsVisible"
            class="module"
            data-test-id="distribution-confirmation-view"
        >
            <header class="module__header">
                <a
                    class="back-btn"
                    data-test-id="distribution-confirmation-back-arrow"
                    @click="confirmationIsVisible = false"
                >
                    <span class="icon-chevron-left" />
                </a>
                <h1 class="has-back-btn">Confirm Distribution</h1>
            </header>
            <section class="module__body">
                <div class="preview">
                    <distribution-preview
                        :configurationSet="configurationSet"
                        :distribution="distributionForPreview"
                    />
                    <div class="form-controls">
                        <button
                            class="btn btn-secondary"
                            data-test-id="distribution-confirmation-back-button"
                            @click.prevent="confirmationIsVisible = false"
                        >
                            <div>Previous</div>
                        </button>
                        <button
                            type="button"
                            class="btn btn-primary"
                            data-test-id="distribution-submit"
                            :disabled="isSubmitted || !confirmationIsVisible"
                            :class="{ active: isSubmitted }"
                            @click.prevent="submitPreview"
                        >
                            <div>Distribute</div>
                        </button>
                    </div>
                </div>
            </section>
        </div>
        <div
            v-else
            class="module"
        >
            <header class="module__header">
                <a
                    class="back-btn"
                    data-test-id="distribution-create-edit-back-arrow"
                    @click="goBack('admin-configuration-sets')"
                >
                    <span class="icon-chevron-left" />
                </a>
                <h1 class="has-back-btn">Distribute Configuration Set</h1>
            </header>

            <section
                v-if="formLoaded"
                class="module__body"
            >
                <h1 data-test-id="configuration-set-name">{{ configurationSet.name }}</h1>
                <div
                    v-if="allGroups.length === 0"
                    class="no-groups-error"
                    data-test-id="no-groups-error"
                >
                    <p>
                        <span>No Groups Created</span>
                        You must create a group to distribute a Configuration Set.
                    </p>
                    <div class="form-group">
                        <button
                            class="btn btn-medium"
                            data-test-id="navigate-to-create-group-button"
                            @click.prevent="goToCreateGroup"
                        >
                            Create Group
                        </button>
                    </div>
                </div>
                <form
                    v-else
                    class="full-width"
                    data-test-id="distribution-form"
                >
                    <div class="form-group">
                        <label for="name">Distribution Name</label>
                        <input
                            id="name"
                            ref="nameFieldRef"
                            v-model="name"
                            type="text"
                            data-test-id="distribution-name"
                            required
                            minlength="3"
                            maxlength="20"
                        />
                    </div>

                    <div class="form-group multi-select">
                        <multi-select-field
                            ref="groupSelectRef"
                            :allItems="allGroups"
                            label="Groups"
                            labelKey="name"
                            testId="groups-select"
                            :selectedValues="selectedGroupsArray"
                            :invalid="showMissingTargetError"
                            valueKey="id"
                            @items-selected="handleGroupsSelected"
                            @items-unselected="handleGroupsUnselected"
                        ></multi-select-field>
                        <div
                            v-if="showMissingTargetError"
                            class="inline-error"
                            data-test-id="target-validation-error"
                        >
                            <p data-test-id="target-validation-error-text">
                                {{ missingTargetValidationMessage }}
                            </p>
                        </div>
                    </div>

                    <div
                        v-if="advancedRelaysAreEnabled"
                        class="form-group multi-select"
                        data-test-id="advanced-relays-container"
                    >
                        <multi-select-field
                            ref="assetsSelectRef"
                            :allItems="allAssets"
                            label="AdvancedRelays"
                            labelKey="name"
                            testId="assets-select"
                            :selectedValues="selectedAssetsArray"
                            :invalid="showMissingTargetError"
                            valueKey="id"
                            @items-selected="handleAssetsSelected"
                            @items-unselected="handleAssetsUnselected"
                        ></multi-select-field>
                        <div
                            v-if="showMissingTargetError"
                            class="inline-error"
                            data-test-id="target-validation-error"
                        >
                            <p data-test-id="target-validation-error-text">
                                Please choose at least one AdvancedRelay OR Group.
                            </p>
                        </div>
                    </div>

                    <div class="form-group">
                        <slider-toggle-field
                            label="Distribute Now"
                            description="When toggled on, Configuration Set will be distributed immediately. Toggle
                            off to set a custom distribution time."
                            :value="distributeNow"
                            data-test-id="distribute-now-toggle"
                            @update="toggleDistributeNow"
                        ></slider-toggle-field>
                    </div>

                    <div
                        v-if="!distributeNow"
                        class="form-group"
                    >
                        <label for="distribute_at">Distribution Time</label>
                        <DatePicker
                            ref="distributeAtRef"
                            v-model="distribute_at"
                            dateFormat="mm-dd-yy"
                            fluid
                            hourFormat="24"
                            iconDisplay="input"
                            inputId="distribute_at"
                            :maxDate="maxDistributeAt"
                            :minDate="minDistributeAt"
                            placeholder="MM-DD-YYYY HH:MM"
                            :pt="{ pcInputText: { root: { 'data-test-id': 'distribute-at-input', required: true } } }"
                            showIcon
                            :showOnFocus="false"
                            showTime
                        >
                            <template #inputicon="{ clickCallback }">
                                <img
                                    alt="Select"
                                    class="calendar-input-icon"
                                    data-test-id="distribute_at_icon"
                                    src="/images/icon-calendar.svg"
                                    @click="clickCallback"
                                />
                            </template>
                        </DatePicker>
                        <span
                            class="datetime-info"
                            data-test-id="datetime-info-distribute-at"
                        >
                            <span class="label">Time Zone: </span> {{ timezoneLabel }}
                        </span>
                    </div>

                    <div
                        v-if="distributeNow"
                        class="form-group"
                    >
                        <slider-toggle-field
                            label="Activate Now"
                            description="When toggled on, Configuration Set will be activated immediately upon
                            device receipt. Toggle off to set a custom activation time."
                            :value="activateNow"
                            data-test-id="activate-now-toggle"
                            @update="toggleActivateNow"
                        ></slider-toggle-field>
                    </div>

                    <div
                        v-if="!activateNow"
                        class="form-group"
                    >
                        <label for="activate_at">Activation Time</label>
                        <DatePicker
                            ref="activateAtRef"
                            v-model="activate_at"
                            dateFormat="mm-dd-yy"
                            fluid
                            hourFormat="24"
                            iconDisplay="input"
                            inputId="activate_at"
                            :maxDate="maxActivateAt"
                            :minDate="minActivateAt"
                            placeholder="MM-DD-YYYY HH:MM"
                            :pt="{ pcInputText: { root: { 'data-test-id': 'activate-at-input', required: true } } }"
                            showIcon
                            :showOnFocus="false"
                            showTime
                        >
                            <template #inputicon="{ clickCallback }">
                                <img
                                    alt="Select"
                                    data-test-id="activate_at_icon"
                                    class="calendar-input-icon"
                                    src="/images/icon-calendar.svg"
                                    @click="clickCallback"
                                />
                            </template>
                        </DatePicker>
                        <span
                            class="datetime-info"
                            data-test-id="datetime-info-activate-at"
                        >
                            <span class="label">Time Zone: </span> {{ timezoneLabel }}
                        </span>
                    </div>
                    <div class="form-group">
                        <label for="expire_at">Expiration Time (optional)</label>
                        <DatePicker
                            v-model="expire_at"
                            dateFormat="mm-dd-yy"
                            fluid
                            hourFormat="24"
                            iconDisplay="input"
                            inputId="expire_at"
                            :minDate="minExpireAt"
                            placeholder="MM-DD-YYYY HH:MM"
                            :pt="{ pcInputText: { root: { 'data-test-id': 'expire-at-input' } } }"
                            showIcon
                            :showOnFocus="false"
                            showTime
                        >
                            <template #inputicon="{ clickCallback }">
                                <img
                                    alt="Select"
                                    data-test-id="expire_at_icon"
                                    class="calendar-input-icon"
                                    src="/images/icon-calendar.svg"
                                    @click="clickCallback"
                                />
                            </template>
                        </DatePicker>
                        <span
                            class="datetime-info"
                            data-test-id="datetime-info-expire-at"
                        >
                            <span class="label">Time Zone: </span> {{ timezoneLabel }}
                        </span>
                    </div>

                    <form-errors :response="errorResponse" />

                    <div class="form-group form-controls">
                        <button
                            type="button"
                            class="btn btn-primary"
                            :class="{ active: isSubmitted }"
                            data-test-id="distribution-review"
                            :disabled="isSubmitted || confirmationIsVisible"
                            @click.prevent="handleFormSubmit"
                        >
                            <div>Review</div>
                        </button>
                    </div>
                </form>
            </section>
        </div>
    </div>
</template>

<script>
import { isUndefined, uniqBy } from 'lodash'
import DatePicker from 'primevue/datepicker'
import MultiSelectField from '@/components/MultiSelectField.vue'
import FormErrors from '@/components/FormErrors.vue'
import SliderToggleField from '@/components/SliderToggleField.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import DistributionPreview from '@/components/ConfigurationSets/DistributionPreview.vue'
import featureFlags from '../../../../constants/feature-flags.js'

export default {
    components: {
        DatePicker,
        FormErrors,
        MultiSelectField,
        SliderToggleField,
        DistributionPreview,
    },

    mixins: [canToast, hasBackButton, needsOrganizationSlug],

    data() {
        return {
            configurationSetId: this.$route.params.configurationSetId,
            distributionId: this.$route.params.configurationSetDistributionId ?? null,
            cloneDistributionId: this.$route.query?.cloneId ?? null,
            configurationSet: null,
            allGroups: [],
            allAssets: [],
            selectedGroups: new Set(),
            selectedAssets: new Set(),
            name: null,
            distributeNow: isUndefined(this.$route.params.configurationSetDistributionId),
            activateNow: isUndefined(this.$route.params.configurationSetDistributionId),
            distribute_at: null,
            activate_at: null,
            expire_at: null,
            formLoaded: false,
            isSubmitted: false,
            errorResponse: null,
            confirmationIsVisible: false,
            showMissingTargetError: false,
        }
    },

    computed: {
        advancedRelaysAreEnabled() {
            return this.$store.getters.hasFeatureFlag(featureFlags.ADVANCED_RELAYS_ENABLED)
        },
        missingTargetValidationMessage() {
            return this.advancedRelaysAreEnabled
                ? 'Please choose at least one AdvancedRelay OR Group.'
                : 'Please choose at least one Group.'
        },
        selectedGroupsArray() {
            return Array.from(this.selectedGroups)
        },
        selectedAssetsArray() {
            return Array.from(this.selectedAssets)
        },
        timezoneLabel() {
            const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
            let label = 'Unknown'

            if (timeZone) {
                label = timeZone.replace('_', ' ')
            }

            return label
        },
        minDistributeAt() {
            return new Date()
        },
        maxDistributeAt() {
            return this.activate_at ?? this.expire_at
        },
        minActivateAt() {
            return this.distribute_at ?? new Date()
        },
        maxActivateAt() {
            return this.expire_at
        },
        minExpireAt() {
            // get the max non-null date
            let max = new Date()
            if (this.distribute_at && this.distribute_at > max) max = this.distribute_at
            if (this.activate_at && this.activate_at > max) max = this.activate_at
            return max
        },
        isEditing() {
            return this.distributionId !== null
        },
        isCloning() {
            return this.cloneDistributionId !== null
        },
        distributionForPreview() {
            return {
                name: this.name,
                distribute_now: this.distributeNow,
                activate_now: this.activateNow,
                distribute_at: this.distribute_at,
                activate_at: this.activate_at,
                expire_at: this.expire_at,
                groups: this.selectedGroupsArray.map((id) => {
                    const group = this.allGroups.find((g) => g.id === id)
                    if (group) {
                        return {
                            id: group.id,
                            name: group.name,
                        }
                    }
                    return {
                        id: -1,
                        name: 'Group Not Found',
                    }
                }),
                assets: this.selectedAssetsArray.map((id) => {
                    const asset = this.allAssets.find((a) => a.id === id)
                    if (asset) {
                        return {
                            id: asset.id,
                            name: asset.name,
                        }
                    }
                    return {
                        id: -1,
                        name: 'Asset Not Found',
                    }
                }),
            }
        },
    },

    mounted() {
        const requests = [
            this.$http.get(`/${this.organizationSlug}/user-groups/all`),
            this.$http.get(`/${this.organizationSlug}/configuration-sets/${this.configurationSetId}`),
            this.$http.get(`/${this.organizationSlug}/assets/smart-devices`),
        ]

        if (this.isEditing || this.isCloning) {
            const distributionId = this.distributionId || this.cloneDistributionId

            requests.push(
                this.$http.get(
                    `/${this.organizationSlug}/configuration-sets/${this.configurationSetId}/` +
                        `distributions/${distributionId}`
                )
            )
        }

        Promise.all(requests)
            .then(([groupsResponse, configurationSetResponse, assetsResponse, distributionResponse]) => {
                this.allGroups = groupsResponse.data
                this.configurationSet = configurationSetResponse.data
                this.allAssets = assetsResponse.data

                if (distributionResponse) {
                    const distribution = distributionResponse.data

                    const { name, groups, assets } = distribution
                    const distributeAt = distribution.distribute_at ? new Date(distribution.distribute_at) : null
                    const activateAt = distribution.activate_at ? new Date(distribution.activate_at) : null
                    const expireAt = distribution.expire_at ? new Date(distribution.expire_at) : null

                    this.distributeNow = false
                    groups.forEach((group) => {
                        this.selectedGroups.add(group.id)
                    })
                    assets.forEach((group) => {
                        this.selectedAssets.add(group.id)
                    })

                    if (this.isEditing) {
                        this.name = name
                        this.distributeNow = false
                        this.distribute_at = distributeAt
                        this.activate_at = activateAt
                        this.expire_at = expireAt

                        this.allGroups = uniqBy([...distribution.groups, ...this.allGroups], 'id')
                        this.allAssets = uniqBy([...distribution.assets, ...this.allAssets], 'id')
                    } else if (this.isCloning) {
                        const now = new Date()
                        if (distributeAt <= now) {
                            this.distributeNow = true
                        } else {
                            this.distributeNow = false
                            this.distribute_at = distributeAt
                        }

                        if (activateAt > now) {
                            this.activate_at = activateAt
                        }

                        if (expireAt != null && expireAt > now) {
                            this.expire_at = expireAt
                        }
                    }
                }

                this.formLoaded = true
            })
            .catch((error) => {
                this.toast('There was a problem loading data for this page.', 'error')
            })
    },

    methods: {
        createDistribution() {
            this.isSubmitted = true

            this.$http
                .post(`/${this.organizationSlug}/configuration-sets/${this.configurationSetId}/distributions`, {
                    groups: Array.from(this.selectedGroups),
                    assets: Array.from(this.selectedAssets),
                    name: this.name,
                    distribute_now: this.distributeNow,
                    activate_now: this.activateNow,
                    distribute_at: this.distribute_at !== null ? this.dateToUtcIso8601(this.distribute_at) : null,
                    activate_at: this.activate_at !== null ? this.dateToUtcIso8601(this.activate_at) : null,
                    expire_at: this.expire_at !== null ? this.dateToUtcIso8601(this.expire_at) : null,
                })
                .then(() => {
                    this.toast('Distribution successfully created')
                    this.$router.push(
                        `/${this.organizationSlug}/configuration-sets/${this.configurationSetId}/distributions`
                    )
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.confirmationIsVisible = false
                    this.isSubmitted = false
                })
        },
        updateDistribution() {
            this.isSubmitted = true

            this.$http
                .put(
                    `/${this.organizationSlug}/configuration-sets/${this.configurationSetId}` +
                        `/distributions/${this.distributionId}`,
                    {
                        id: this.distributionId,
                        groups: Array.from(this.selectedGroups),
                        assets: Array.from(this.selectedAssets),
                        name: this.name,
                        distribute_now: this.distributeNow,
                        activate_now: this.activateNow,
                        distribute_at: this.distribute_at !== null ? this.dateToUtcIso8601(this.distribute_at) : null,
                        activate_at: this.activate_at !== null ? this.dateToUtcIso8601(this.activate_at) : null,
                        expire_at: this.expire_at !== null ? this.dateToUtcIso8601(this.expire_at) : null,
                    }
                )
                .then(() => {
                    this.toast('Distribution successfully updated')
                    this.$router.push(
                        `/${this.organizationSlug}/configuration-sets/${this.configurationSetId}/distributions`
                    )
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.confirmationIsVisible = false
                    this.isSubmitted = false
                })
        },
        dateToUtcIso8601(date) {
            // compatible with PHP ATOM time
            return window.moment(date).utc().format('YYYY-MM-DDTHH:mm:ssZ')
        },
        handleGroupsSelected(ids) {
            ids.forEach((id) => {
                this.selectedGroups.add(id)
            })
        },
        handleGroupsUnselected(ids) {
            ids.forEach((id) => {
                this.selectedGroups.delete(id)
            })
        },
        handleAssetsSelected(ids) {
            ids.forEach((id) => {
                this.selectedAssets.add(id)
            })
        },
        handleAssetsUnselected(ids) {
            ids.forEach((id) => {
                this.selectedAssets.delete(id)
            })
        },
        toggleDistributeNow() {
            this.distributeNow = !this.distributeNow
            if (this.distributeNow) {
                this.distribute_at = null
            } else {
                this.activateNow = false
            }
        },
        toggleActivateNow() {
            this.activateNow = !this.activateNow
            if (this.activateNow) {
                this.activate_at = null
            }
        },
        goToCreateGroup() {
            this.$router.push(`/${this.organizationSlug}/user-groups/create`)
        },
        submitPreview() {
            if (this.isEditing) {
                this.updateDistribution()
            } else {
                this.createDistribution()
            }
        },
        distributionForApi() {
            return {
                groups: Array.from(this.selectedGroups),
                assets: Array.from(this.selectedAssets),
                name: this.name,
                distribute_now: this.distributeNow,
                activate_now: this.activateNow,
                distribute_at: this.distribute_at !== null ? this.dateToUtcIso8601(this.distribute_at) : null,
                activate_at: this.activate_at !== null ? this.dateToUtcIso8601(this.activate_at) : null,
                expire_at: this.expire_at !== null ? this.dateToUtcIso8601(this.expire_at) : null,
            }
        },
        formIsValid() {
            if (!this.$refs.nameFieldRef.reportValidity()) {
                return false
            }

            if (!this.distributeNow && !this.$refs.distributeAtRef.input.reportValidity()) {
                return false
            }

            if (!this.activateNow && !this.$refs.activateAtRef.input.reportValidity()) {
                return false
            }

            if (this.selectedGroups.size === 0 && this.selectedAssets.size === 0) {
                this.showMissingTargetError = true

                this.$refs.groupSelectRef.$el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                })
                return false
            }

            return true
        },
        handleFormSubmit() {
            if (this.formIsValid()) {
                this.isSubmitted = true
                const url = this.isEditing
                    ? `/${this.organizationSlug}/configuration-sets/${this.configurationSetId}/distributions/${this.distributionId}/validate-distribution`
                    : `/${this.organizationSlug}/configuration-sets/${this.configurationSetId}/validate-distribution`
                const method = this.isEditing ? 'put' : 'post'

                this.$http[method](url, this.distributionForApi())
                    .then(() => {
                        this.confirmationIsVisible = true
                        this.isSubmitted = false
                        this.errorResponse = null
                    })
                    .catch((error) => {
                        this.errorResponse = error.response
                        this.confirmationIsVisible = false
                        this.isSubmitted = false
                    })
            }
        },
    },
}
</script>
<style scoped lang="scss">
.calendar-input-icon {
    margin-top: -5px;
}
.module__body {
    form {
        max-width: 360px;

        .datetime-info {
            margin-top: 10px;
            font-size: 14px;
            line-height: 20px;
            display: flex;

            .label {
                font-family: 'Avenir Heavy';
                margin-right: 1em;
            }

            &:before {
                content: '';
                background-image: url(@images/icon-info.svg);
                background-size: 17px 17px;
                background-repeat: no-repeat;
                background-position: top left;
                width: 17px;
                height: 17px;
                margin-right: 14px;
                display: block;
                flex-shrink: 0;
            }
        }
    }

    h1 {
        font-size: 24px;
        font-weight: 400;
        line-height: 32px;
        font-family: 'Avenir', sans-serif;
        margin-bottom: 20px;
    }

    .no-groups-error {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        background: #8c1d18;
        width: 100%;
        height: 92px;
        border-radius: 8px;
        padding: 16px 24px;

        p {
            width: 213px;
            font-size: 14px;

            span {
                font-family: 'Avenir Heavy';
                display: block;
            }
        }
    }
}
.preview {
    max-width: 420px;
    .form-controls {
        margin-top: 24px;
        width: 360px;
    }
}
.form-controls {
    display: flex;
    gap: 24px;
    justify-content: flex-end;
    &:after {
        content: unset;
    }
}
</style>
